import { useNavigate } from '@rocketlane/rocket-router';
import { useCallback } from 'react';
import { ROUTE_PATHS } from 'routes/route-paths';

const isModuleFederation =
  typeof process !== 'undefined' && process.env.IS_MODULE_FEDERATION === 'true';

const getAccountDetailRoute = (
  companyId: number,
  projectId?: number,
  isV2?: boolean,
) => {
  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    const id = projectId ?? companyId;
    const segment = isV2 ? 'emails' : 'email-pages';

    return `/projects/${id}/${segment}`;
  }

  return `/${ROUTE_PATHS.ACCOUNTS}/${ROUTE_PATHS.ACCOUNT_DETAIL.replace(
    ':accountId',
    companyId.toString() ?? '',
  )}`;
};
const getBaseEmailPageRoute = (
  companyId: number,
  emailId?: number,
  projectId?: number,
  isV2?: boolean,
) => {
  const baseRoute = getAccountDetailRoute(companyId, projectId, isV2);

  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    return emailId ? `${baseRoute}/${emailId}` : baseRoute;
  }

  const emailsPath = emailId
    ? ROUTE_PATHS.ACCOUNT_DETAIL_EMAILS.replace(':emailId?', emailId.toString())
    : ROUTE_PATHS.ACCOUNT_DETAIL_EMAILS.replace('/:emailId?', '');

  return `${baseRoute}/${emailsPath}`;
};
const getEmailPageListRoute = (
  companyId: number,
  projectId?: number,
  isV2?: boolean,
) => {
  return getBaseEmailPageRoute(companyId, undefined, projectId, isV2);
};
const getEmailPageRoute = (
  companyId: number,
  emailId: number,
  projectId?: number,
  isV2?: boolean,
) => {
  return getBaseEmailPageRoute(companyId, emailId, projectId, isV2);
};
const getEmailPageEditRoute = (
  companyId: number,
  emailId: number,
  projectId?: number,
  isV2?: boolean,
) => {
  const baseRoute = getEmailPageRoute(companyId, emailId, projectId, isV2);

  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    return `${baseRoute}/edit`;
  }

  return `${baseRoute}/${ROUTE_PATHS.ACCOUNT_DETAIL_EMAIL_EDIT}`;
};

export const useEmailPagesRouteNavigations = (
  projectId?: number,
  isV2?: boolean,
) => {
  const navigate = useNavigate();
  const navigateToList = useCallback(
    (companyId: number) => {
      navigate(getEmailPageListRoute(companyId, projectId, isV2));
    },
    [navigate, projectId, isV2],
  );
  const navigateToEmail = useCallback(
    (companyId: number, emailId: number) => {
      navigate(getEmailPageRoute(companyId, emailId, projectId, isV2));
    },
    [navigate, projectId, isV2],
  );
  const navigateToEmailEdit = useCallback(
    (companyId: number, emailId: number) => {
      navigate(getEmailPageEditRoute(companyId, emailId, projectId, isV2));
    },
    [navigate, projectId, isV2],
  );

  return {
    navigateToList,
    navigateToEmail,
    navigateToEmailEdit,
  };
};
